/*!
 * @jscrypto/ciphers v0.9.1
 * Copyright 2026 Chen, Yi-Cyuan
 * Released under the MIT license
 */
/**
 * Utilities for hex, bytes, CSPRNG.
 * @module
 */
/*! noble-ciphers - MIT License (c) 2023 Paul Miller (paulmillr.com) */
/**
 * Checks if something is Uint8Array. Be careful: nodejs Buffer will return true.
 * @param a - Value to inspect.
 * @returns `true` when the value is a Uint8Array view, including Node's `Buffer`.
 * @example
 * Guards a value before treating it as raw key material.
 *
 * ```ts
 * isBytes(new Uint8Array());
 * ```
 */
function isBytes(a) {
    // Plain `instanceof Uint8Array` is too strict for some Buffer / proxy /
    // cross-realm cases. The fallback still requires a real ArrayBuffer view
    // so plain JSON-deserialized `{ constructor: ... }`
    // spoofing is rejected, and `BYTES_PER_ELEMENT === 1` keeps the fallback on byte-oriented views.
    return (a instanceof Uint8Array ||
        (ArrayBuffer.isView(a) &&
            a.constructor.name === 'Uint8Array' &&
            'BYTES_PER_ELEMENT' in a &&
            a.BYTES_PER_ELEMENT === 1));
}
/**
 * Asserts something is Uint8Array.
 * @param value - Value to validate.
 * @param length - Expected byte length.
 * @param title - Optional label used in error messages.
 * @returns The validated byte array.
 * On Node, `Buffer` is accepted too because it is a Uint8Array view.
 * @throws On wrong argument types. {@link TypeError}
 * @throws On wrong argument lengths. {@link RangeError}
 * @example
 * Validates a fixed-length nonce or key buffer.
 *
 * ```ts
 * abytes(new Uint8Array([1, 2]), 2);
 * ```
 */
function abytes(value, length, title = '') {
    const bytes = isBytes(value);
    const len = value?.length;
    const needsLen = length !== undefined;
    if (!bytes || (needsLen && len !== length)) {
        const prefix = title && `"${title}" `;
        const ofLen = needsLen ? ` of length ${length}` : '';
        const got = bytes ? `length=${len}` : `type=${typeof value}`;
        const message = prefix + 'expected Uint8Array' + ofLen + ', got ' + got;
        if (!bytes)
            throw new TypeError(message);
        throw new RangeError(message);
    }
    return value;
}
/**
 * Asserts output is a properly-sized byte array.
 * @param out - Output buffer to validate.
 * @param instance - Hash-like instance providing `outputLen`.
 * This is the relaxed `digestInto()`-style contract: output must be at least `outputLen`,
 * unlike one-shot cipher helpers elsewhere in the repo that often require exact lengths.
 * @throws On wrong argument types. {@link TypeError}
 * @param onlyAligned - Whether `out` must be 4-byte aligned for zero-allocation word views.
 * @throws On wrong output buffer lengths. {@link RangeError}
 * @throws On wrong output buffer alignment. {@link Error}
 * @example
 * Verifies that a caller-provided output buffer is large enough.
 *
 * ```ts
 * aoutput(new Uint8Array(16), { outputLen: 16 });
 * ```
 */
function aoutput(out, instance, onlyAligned = false) {
    abytes(out, undefined, 'output');
    const min = instance.outputLen;
    if (out.length < min) {
        throw new RangeError('digestInto() expects output buffer of length at least ' + min);
    }
    if (onlyAligned && !isAligned32(out))
        throw new Error('invalid output, must be aligned');
}
/**
 * Casts a typed-array view to Uint8Array.
 * @param arr - Typed-array view to reinterpret.
 * @returns Uint8Array view over the same bytes.
 * @example
 * Views 32-bit words as raw bytes without copying.
 *
 * ```ts
 * u8(new Uint32Array([1]));
 * ```
 */
function u8(arr) {
    return new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
}
/**
 * Casts a typed-array view to Uint32Array.
 * @param arr - Typed-array view to reinterpret.
 * @returns Uint32Array view over the same bytes. Callers are expected to provide a
 * 4-byte-aligned offset; trailing `1..3` bytes are silently dropped.
 * @example
 * Views a byte buffer as 32-bit words for block processing.
 *
 * ```ts
 * u32(new Uint8Array(4));
 * ```
 */
function u32(arr) {
    return new Uint32Array(arr.buffer, arr.byteOffset, Math.floor(arr.byteLength / 4));
}
/**
 * Zeroizes typed arrays in place.
 * Warning: JS provides no guarantees.
 * @param arrays - Arrays to wipe.
 * @example
 * Wipes a temporary key buffer after use.
 *
 * ```ts
 * const bytes = new Uint8Array([1]);
 * clean(bytes);
 * ```
 */
function clean(...arrays) {
    for (let i = 0; i < arrays.length; i++) {
        arrays[i].fill(0);
    }
}
/**
 * Creates a DataView for byte-level manipulation.
 * @param arr - Typed-array view to wrap.
 * @returns DataView over the same bytes.
 * @example
 * Creates an endian-aware view for length encoding.
 *
 * ```ts
 * createView(new Uint8Array(4));
 * ```
 */
function createView(arr) {
    return new DataView(arr.buffer, arr.byteOffset, arr.byteLength);
}
/**
 * Whether the current platform is little-endian.
 * Most are; some IBM systems are not.
 */
const isLE = /* @__PURE__ */ (() => new Uint8Array(new Uint32Array([0x11223344]).buffer)[0] === 0x44)();
/**
 * Reverses byte order of one 32-bit word.
 * @param word - Unsigned 32-bit word to swap.
 * @returns The same word with bytes reversed.
 * @example
 * Swaps a big-endian word into little-endian byte order.
 *
 * ```ts
 * byteSwap(0x11223344);
 * ```
 */
const byteSwap = (word) => ((word << 24) & 0xff000000) |
    ((word << 8) & 0xff0000) |
    ((word >>> 8) & 0xff00) |
    ((word >>> 24) & 0xff);
/**
 * Normalizes one 32-bit word to the little-endian representation expected by cipher cores.
 * @param n - Unsigned 32-bit word to normalize.
 * @returns Little-endian normalized word on big-endian hosts, else the input word unchanged.
 * @example
 * Normalizes a host-endian word before passing it into an ARX/AES core.
 *
 * ```ts
 * swap8IfBE(0x11223344);
 * ```
 */
const swap8IfBE = isLE
    ? (n) => n
    : (n) => byteSwap(n) >>> 0;
/**
 * Byte-swaps every word of a Uint32Array in place.
 * @param arr - Uint32Array whose words should be swapped.
 * @returns The same array after in-place byte swapping.
 * @example
 * Swaps every 32-bit word in a word-view buffer.
 *
 * ```ts
 * byteSwap32(new Uint32Array([0x11223344]));
 * ```
 */
const byteSwap32 = (arr) => {
    for (let i = 0; i < arr.length; i++)
        arr[i] = byteSwap(arr[i]);
    return arr;
};
/**
 * Normalizes a Uint32Array view to the little-endian representation expected by cipher cores.
 * @param u - Word view to normalize in place.
 * @returns Little-endian normalized word view.
 * @example
 * Normalizes a word-view buffer before block processing.
 *
 * ```ts
 * swap32IfBE(new Uint32Array([0x11223344]));
 * ```
 */
const swap32IfBE = isLE
    ? (u) => u
    : byteSwap32;
/**
 * Checks if two U8A use same underlying buffer and overlaps.
 * This is invalid and can corrupt data.
 * @param a - First byte view.
 * @param b - Second byte view.
 * @returns `true` when the views overlap in memory.
 * @example
 * Detects whether two slices alias the same backing buffer.
 *
 * ```ts
 * overlapBytes(new Uint8Array(4), new Uint8Array(4));
 * ```
 */
function overlapBytes(a, b) {
    // Zero-length views cannot overwrite anything, even if their offset sits inside another range.
    if (!a.byteLength || !b.byteLength)
        return false;
    return (a.buffer === b.buffer && // best we can do, may fail with an obscure Proxy
        a.byteOffset < b.byteOffset + b.byteLength && // a starts before b end
        b.byteOffset < a.byteOffset + a.byteLength // b starts before a end
    );
}
/**
 * If input and output overlap and input starts before output, we will overwrite end of input before
 * we start processing it, so this is not supported for most ciphers
 * (except chacha/salsa, which were designed for this)
 * @param input - Input bytes.
 * @param output - Output bytes.
 * @throws If the output view would overwrite unread input bytes. {@link Error}
 * @example
 * Rejects an in-place layout that would overwrite unread input bytes.
 *
 * ```ts
 * complexOverlapBytes(new Uint8Array(4), new Uint8Array(4));
 * ```
 */
function complexOverlapBytes(input, output) {
    // This is very cursed. It works somehow, but I'm completely unsure,
    // reasoning about overlapping aligned windows is very hard.
    if (overlapBytes(input, output) && input.byteOffset < output.byteOffset)
        throw new Error('complex overlap of input and output is not supported');
}
/**
 * Wraps a keyed MAC constructor into a one-shot helper with `.create()`.
 * @param keyLen - Valid probe-key length used to read static metadata once.
 * The probe key is only used for `outputLen` / `blockLen`, so callers with several valid key sizes
 * can pass any representative size as long as those values stay fixed.
 * @param macCons - Keyed MAC constructor or factory.
 * @param fromMsg - Optional adapter that derives extra constructor args from the one-shot message.
 * @returns Callable MAC helper with `.create()`.
 */
function wrapMacConstructor(keyLen, macCons, fromMsg) {
    const mac = macCons;
    const getArgs = (fromMsg || (() => []));
    const macC = (msg, key) => mac(key, ...getArgs(msg))
        .update(msg)
        .digest();
    const tmp = mac(new Uint8Array(keyLen), ...getArgs(new Uint8Array(0)));
    macC.outputLen = tmp.outputLen;
    macC.blockLen = tmp.blockLen;
    macC.create = (key, ...args) => mac(key, ...args);
    return macC;
}
/**
 * By default, returns u8a of length.
 * When out is available, it checks it for validity and uses it.
 * @param expectedLength - Required output length.
 * @param out - Optional destination buffer.
 * @param onlyAligned - Whether `out` must be 4-byte aligned.
 * @returns Output buffer ready for writing.
 * @throws On wrong argument types. {@link TypeError}
 * @throws If the provided output buffer has the wrong size or alignment. {@link Error}
 * @example
 * Reuses a caller-provided output buffer when lengths match.
 *
 * ```ts
 * getOutput(16, new Uint8Array(16));
 * ```
 */
function getOutput(expectedLength, out, onlyAligned = true) {
    if (out === undefined)
        return new Uint8Array(expectedLength);
    // Keep Buffer/cross-realm Uint8Array support here instead of trusting a shape-compatible object.
    abytes(out, undefined, 'output');
    if (out.length !== expectedLength)
        throw new Error('"output" expected Uint8Array of length ' + expectedLength + ', got: ' + out.length);
    if (onlyAligned && !isAligned32(out))
        throw new Error('invalid output, must be aligned');
    return out;
}
/**
 * Checks whether a byte array is aligned to a 4-byte offset.
 * @param bytes - Byte array to inspect.
 * @returns `true` when the view is 4-byte aligned.
 * @example
 * Checks whether a buffer can be safely viewed as Uint32Array.
 *
 * ```ts
 * isAligned32(new Uint8Array(4));
 * ```
 */
function isAligned32(bytes) {
    return bytes.byteOffset % 4 === 0;
}
/**
 * Copies bytes into a new Uint8Array.
 * @param bytes - Bytes to copy.
 * @returns Copied byte array.
 * @throws On wrong argument types. {@link TypeError}
 * @example
 * Copies input into an aligned Uint8Array before block processing.
 *
 * ```ts
 * copyBytes(new Uint8Array([1, 2]));
 * ```
 */
function copyBytes(bytes) {
    // `Uint8Array.from(...)` would also accept arrays / other typed arrays. Keep this helper strict
    // because callers use it at byte-validation boundaries before mutating the detached copy.
    return Uint8Array.from(abytes(bytes));
}

/**
 * {@link https://en.wikipedia.org/wiki/Advanced_Encryption_Standard | AES}
 * a.k.a. Advanced Encryption Standard
 * is a variant of Rijndael block cipher, standardized by NIST in 2001.
 * We provide the fastest available pure JS implementation.
 *
 * `cipher = encrypt(block, key)`
 *
 * Data is split into 128-bit blocks.
 * Encrypted in 10/12/14 rounds (128/192/256 bits). In every round:
 * 1. **S-box**, table substitution
 * 2. **Shift rows**, cyclic shift left of all rows of data array
 * 3. **Mix columns**, multiplying every column by fixed polynomial
 * 4. **Add round key**, round_key xor i-th column of array
 *
 * Check out
 * {@link https://csrc.nist.gov/files/pubs/fips/197/final/docs/fips-197.pdf | FIPS-197},
 * {@link https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-38G.pdf | NIST 800-38G},
 * and {@link https://csrc.nist.gov/csrc/media/projects/cryptographic-standards-and-guidelines/documents/aes-development/rijndael-ammended.pdf | original proposal}.
 * @module
 */
const BLOCK_SIZE$1 = 16;
// AES operates on 16-byte blocks, i.e. 4 32-bit words.
const BLOCK_SIZE32 = 4;
// Shared zero block (`0^128`) used by GCM's `H = CIPH_K(0^128)` / J0 scratch
// and by CMAC / SIV helpers; callers take `.slice()` before mutating it.
const EMPTY_BLOCK = /* @__PURE__ */ new Uint8Array(BLOCK_SIZE$1);
// RFC 5297 §2.1 / §2.4: S2V uses `<one> = 0^127 || 1` for the `n = 0` special case.
const ONE_BLOCK = /* @__PURE__ */ Uint8Array.from([
    0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00, 0x01,
]);
const POLY = 0x11b; // 1 + x + x**3 + x**4 + x**8
// Validates plain AES key sizes only; AES-SIV's doubled-key contract is checked elsewhere.
function validateKeyLength(key) {
    if (![16, 24, 32].includes(key.length))
        throw new Error('"aes key" expected Uint8Array of length 16/24/32, got length=' + key.length);
}
// TODO: remove multiplication, binary ops only
// Doubles one GF(2^8) field element; callers are expected to stay in byte range.
// FIPS 197 upd1 §4.3 equation (4.5): XTIMES(b) left-shifts by one and, when
// b7=1, reduces by m(x); using POLY=0x11b here yields the same byte result
// as XORing with {1b} after the shift.
function mul2(n) {
    return (n << 1) ^ (POLY & -(n >> 7));
}
// Shift-and-add multiplication in GF(2^8); callers are expected to pass byte values.
// FIPS 197 upd1 §4.3 equation (4.7): general products are XORs of repeated
// XTIMES() multiples, e.g. {57}•{13} = {57}⊕{ae}⊕{07}.
function mul(a, b) {
    let res = 0;
    for (; b > 0; b >>= 1) {
        // Usual shift-and-add step in GF(2^8), not a scalar-multiplication ladder.
        res ^= a & -(b & 1); // if (b&1) res ^=a (but const-time).
        a = mul2(a); // a = 2*a
    }
    return res;
}
/**
 * Increments a counter block with wrap around.
 * AES call sites here currently use the big-endian branch, but the helper supports both layouts.
 * NIST SP 800-38A Appendix B.1 and SP 800-38D §6.2 increment the
 * least-significant/rightmost bits.
 * `isLE=false` matches that standard counter-block layout, while `isLE=true`
 * is a generic extension for non-AES callers.
 * The implementation keeps a 32-bit bitwise carry path, so `carry` is capped at `0xffffff00`;
 * larger values throw instead of silently overflowing before the next-byte propagation step.
 */
// Keep the helper explicitly typed so `--isolatedDeclarations` can expose it
// through the test-only `__TESTS` export without inference errors.
const incBytes = (data, isLE, carry = 1) => {
    // Keep `carry + byte <= 0xffffffff` so the `| 0` / `>>> 8` path below
    // never truncates a real carry bit.
    if (!Number.isSafeInteger(carry) || carry > 0xffffff00)
        throw new Error('incBytes: wrong carry ' + carry);
    abytes(data);
    for (let i = 0; i < data.length; i++) {
        const pos = !isLE ? data.length - 1 - i : i;
        carry = (carry + (data[pos] & 0xff)) | 0;
        data[pos] = carry & 0xff;
        carry >>>= 8;
    }
};
// AES S-box is generated using finite field inversion,
// an affine transform, and xor of a constant 0x63.
const sbox = /* @__PURE__ */ (() => {
    const t = new Uint8Array(256);
    // Repeated multiplication by {03} walks all 255 nonzero field elements
    // once, so t[255 - i] is the multiplicative inverse of t[i] for the
    // affine step.
    for (let i = 0, x = 1; i < 256; i++, x ^= mul2(x))
        t[i] = x;
    const box = new Uint8Array(256);
    // FIPS 197 upd1 §5.1.1: SBOX({00}) = {63} because the inverse step leaves
    // {00} at {00}, then the affine transform xors in c = {63}.
    box[0] = 0x63;
    for (let i = 0; i < 255; i++) {
        let x = t[255 - i];
        x |= x << 8;
        box[t[i]] = (x ^ (x >> 4) ^ (x >> 5) ^ (x >> 6) ^ (x >> 7) ^ 0x63) & 0xff;
    }
    clean(t);
    return box;
})();
// FIPS 197 upd1 §5.3.2: INVSBOX() is derived from SBOX() by swapping input
// and output roles (Table 6).
// `indexOf` is only used once at module init, so the quadratic setup cost stays off hot paths.
const invSbox = /* @__PURE__ */ sbox.map((_, j) => sbox.indexOf(j));
// FIPS 197 upd1 §5.2: ROTWORD([a0,a1,a2,a3]) = [a1,a2,a3,a0]; with this LE
// word packing that is a right rotate by 8 bits.
const rotr32_8 = (n) => (n << 24) | (n >>> 8);
// LE T-table helper: rotates one precomputed word by one byte so T1/T2/T3
// reuse T0's substitution/mix result in the other byte lanes.
const rotl32_8 = (n) => (n << 8) | (n >>> 24);
// T-table is optimization suggested in 5.2 of original proposal (missed from FIPS-197). Changes:
// - LE instead of BE
// - bigger tables: T0 and T1 are merged into T01 table and T2 & T3 into T23;
//   so index is u16, instead of u8. This speeds up things, unexpectedly
function genTtable(sbox, fn) {
    if (sbox.length !== 256)
        throw new Error('Wrong sbox length');
    const T0 = new Uint32Array(256).map((_, j) => fn(sbox[j]));
    const T1 = T0.map(rotl32_8);
    const T2 = T1.map(rotl32_8);
    const T3 = T2.map(rotl32_8);
    // Pre-xor adjacent lanes so apply0123/applySbox can fetch two substituted
    // byte lanes per lookup in the LE round layout.
    const T01 = new Uint32Array(256 * 256);
    const T23 = new Uint32Array(256 * 256);
    const sbox2 = new Uint16Array(256 * 256);
    for (let i = 0; i < 256; i++) {
        for (let j = 0; j < 256; j++) {
            const idx = i * 256 + j;
            T01[idx] = T0[i] ^ T1[j];
            T23[idx] = T2[i] ^ T3[j];
            sbox2[idx] = (sbox[i] << 8) | sbox[j];
        }
    }
    return { sbox, sbox2, T0, T1, T2, T3, T01, T23 };
}
// Forward round precompute: the packed word stores the MIXCOLUMNS row
// [{02},{01},{01},{03}] in LE byte-lane order, and the returned `sbox2`
// is also reused by key expansion and the final round.
const tableEncoding = /* @__PURE__ */ genTtable(sbox, (s) => (mul(s, 3) << 24) | (s << 16) | (s << 8) | mul(s, 2));
// Inverse round precompute: the packed word stores the INVMIXCOLUMNS row
// [{0e},{09},{0d},{0b}] in LE byte-lane order, and the tables are reused
// by decrypt() and expandKeyDecLE().
const tableDecoding = /* @__PURE__ */ genTtable(invSbox, (s) => (mul(s, 11) << 24) | (mul(s, 13) << 16) | (mul(s, 9) << 8) | mul(s, 14));
// FIPS 197 upd1 §5.2 Table 5: left-most bytes of Rcon[j] = x^(j-1), generated by repeated XTIMES().
const xPowers = /* @__PURE__ */ (() => {
    const p = new Uint8Array(16);
    for (let i = 0, x = 1; i < 16; i++, x = mul2(x))
        p[i] = x;
    return p;
})();
/** Forward AES key expansion used across ECB/CBC/CTR/GCM/CMAC/KW-style paths. */
function expandKeyLE(key) {
    abytes(key);
    const len = key.length;
    validateKeyLength(key);
    const { sbox2 } = tableEncoding;
    const toClean = [];
    // Copy on BE or misaligned inputs so the LE word normalization below never
    // mutates caller key bytes in place.
    if (!isLE || !isAligned32(key))
        toClean.push((key = copyBytes(key)));
    const k32 = swap32IfBE(u32(key));
    const Nk = k32.length;
    // `applySbox` normally reads one byte lane from each argument; repeating
    // `n` across all four lanes turns it into SUBWORD(n).
    const subByte = (n) => applySbox(sbox2, n, n, n, n);
    // AES key sizes are 16/24/32 bytes, so len + 28 yields the 44/52/60
    // schedule words from FIPS 197 §5.2 / Table 3.
    const xk = new Uint32Array(len + 28); // expanded key
    xk.set(k32);
    // 4.3.1 Key expansion
    for (let i = Nk; i < xk.length; i++) {
        let t = xk[i - 1];
        if (i % Nk === 0)
            t = subByte(rotr32_8(t)) ^ xPowers[i / Nk - 1];
        else if (Nk > 6 && i % Nk === 4)
            t = subByte(t);
        xk[i] = xk[i - Nk] ^ t;
    }
    clean(...toClean);
    return xk;
}
function expandKeyDecLE(key) {
    const encKey = expandKeyLE(key);
    const xk = encKey.slice();
    const Nk = encKey.length;
    const { sbox2 } = tableEncoding;
    const { T0, T1, T2, T3 } = tableDecoding;
    // Local decrypt() walks round keys forward from xk[0], so reverse the
    // encryption round-key blocks first before applying the equivalent-inverse
    // middle-round transform.
    for (let i = 0; i < Nk; i += 4) {
        for (let j = 0; j < 4; j++)
            xk[i + j] = encKey[Nk - i - 4 + j];
    }
    clean(encKey);
    // Apply InvMixColumn to the reversed round keys using the same LE sbox2
    // packing as the forward path.
    // apply InvMixColumn except first & last round
    for (let i = 4; i < Nk - 4; i++) {
        const x = xk[i];
        const w = applySbox(sbox2, x, x, x, x);
        xk[i] = T0[w & 0xff] ^ T1[(w >>> 8) & 0xff] ^ T2[(w >>> 16) & 0xff] ^ T3[w >>> 24];
    }
    return xk;
}
// Apply tables
function apply0123(T01, T23, s0, s1, s2, s3) {
    // `T01` takes the low byte lane from `s0` plus the next lane from `s1`;
    // `T23` does the same for `s2`/`s3`.
    // Equivalent to `T0[s0&0xff] ^ T1[(s1>>>8)&0xff] ^ T2[(s2>>>16)&0xff] ^
    // T3[s3>>>24]`, but with two merged-table fetches.
    return (T01[((s0 << 8) & 0xff00) | ((s1 >>> 8) & 0xff)] ^
        T23[((s2 >>> 8) & 0xff00) | ((s3 >>> 24) & 0xff)]);
}
function applySbox(sbox2, s0, s1, s2, s3) {
    // `sbox2` packs two substituted byte lanes at a time in the same LE
    // layout used by the round code.
    // Equivalent to `SBOX(byte0(s0)) | SBOX(byte1(s1))<<8 |
    // SBOX(byte2(s2))<<16 | SBOX(byte3(s3))<<24`.
    return (sbox2[(s0 & 0xff) | (s1 & 0xff00)] |
        (sbox2[((s2 >>> 16) & 0xff) | ((s3 >>> 16) & 0xff00)] << 16));
}
function encrypt(xk, s0, s1, s2, s3) {
    const { sbox2, T01, T23 } = tableEncoding;
    let k = 0;
    ((s0 ^= xk[k++]), (s1 ^= xk[k++]), (s2 ^= xk[k++]), (s3 ^= xk[k++]));
    // `xk` has Nr+1 round-key blocks, so after the initial AddRoundKey and the
    // final S-box-only round there are Nr-1 full table/MixColumns rounds left.
    const rounds = xk.length / 4 - 2;
    for (let i = 0; i < rounds; i++) {
        const t0 = xk[k++] ^ apply0123(T01, T23, s0, s1, s2, s3);
        const t1 = xk[k++] ^ apply0123(T01, T23, s1, s2, s3, s0);
        const t2 = xk[k++] ^ apply0123(T01, T23, s2, s3, s0, s1);
        const t3 = xk[k++] ^ apply0123(T01, T23, s3, s0, s1, s2);
        ((s0 = t0), (s1 = t1), (s2 = t2), (s3 = t3));
    }
    // last round (without mixcolumns, so using SBOX2 table)
    const t0 = xk[k++] ^ applySbox(sbox2, s0, s1, s2, s3);
    const t1 = xk[k++] ^ applySbox(sbox2, s1, s2, s3, s0);
    const t2 = xk[k++] ^ applySbox(sbox2, s2, s3, s0, s1);
    const t3 = xk[k++] ^ applySbox(sbox2, s3, s0, s1, s2);
    return { s0: t0, s1: t1, s2: t2, s3: t3 };
}
// Can't be merged with encrypt: arg positions for apply0123 / applySbox are different
function decrypt(xk, s0, s1, s2, s3) {
    const { sbox2, T01, T23 } = tableDecoding;
    let k = 0;
    ((s0 ^= xk[k++]), (s1 ^= xk[k++]), (s2 ^= xk[k++]), (s3 ^= xk[k++]));
    // With `expandKeyDecLE()` the round keys are already reversed and middle
    // rounds are InvMixColumns-adjusted, so this loop follows the equivalent
    // inverse cipher order directly.
    const rounds = xk.length / 4 - 2;
    for (let i = 0; i < rounds; i++) {
        const t0 = xk[k++] ^ apply0123(T01, T23, s0, s3, s2, s1);
        const t1 = xk[k++] ^ apply0123(T01, T23, s1, s0, s3, s2);
        const t2 = xk[k++] ^ apply0123(T01, T23, s2, s1, s0, s3);
        const t3 = xk[k++] ^ apply0123(T01, T23, s3, s2, s1, s0);
        ((s0 = t0), (s1 = t1), (s2 = t2), (s3 = t3));
    }
    // Final equivalent-inverse round omits InvMixColumns, so use inverse
    // S-box lanes in InvShiftRows order.
    const t0 = xk[k++] ^ applySbox(sbox2, s0, s3, s2, s1);
    const t1 = xk[k++] ^ applySbox(sbox2, s1, s0, s3, s2);
    const t2 = xk[k++] ^ applySbox(sbox2, s2, s1, s0, s3);
    const t3 = xk[k++] ^ applySbox(sbox2, s3, s2, s1, s0);
    return { s0: t0, s1: t1, s2: t2, s3: t3 };
}
function ctrCounter(xk, nonce, src, dst) {
    abytes(nonce, BLOCK_SIZE$1, 'nonce');
    abytes(src);
    const srcLen = src.length;
    dst = getOutput(srcLen, dst);
    complexOverlapBytes(src, dst);
    // Internal helper: mutate `nonce` in place as the live counter block so
    // each encrypted block uses the next CTR value.
    const ctr = nonce;
    const c32 = u32(ctr);
    const src32 = u32(src);
    const dst32 = u32(dst);
    // Fill block (empty, ctr=0)
    let { s0, s1, s2, s3 } = encrypt(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3]));
    // process blocks
    for (let i = 0; i + 4 <= src32.length; i += 4) {
        dst32[i + 0] = src32[i + 0] ^ swap8IfBE(s0);
        dst32[i + 1] = src32[i + 1] ^ swap8IfBE(s1);
        dst32[i + 2] = src32[i + 2] ^ swap8IfBE(s2);
        dst32[i + 3] = src32[i + 3] ^ swap8IfBE(s3);
        incBytes(ctr, false, 1); // Full 128 bit counter with wrap around
        ({ s0, s1, s2, s3 } = encrypt(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3])));
    }
    // NIST SP 800-38A CTR mode uses the leading `u` bits of the next output
    // block for the final short block.
    // It's possible to handle > u32 fast, but is it worth it?
    const start = BLOCK_SIZE$1 * Math.floor(src32.length / BLOCK_SIZE32);
    if (start < srcLen) {
        const b32 = new Uint32Array([s0, s1, s2, s3]);
        swap32IfBE(b32);
        const buf = u8(b32);
        for (let i = start, pos = 0; i < srcLen; i++, pos++)
            dst[i] = src[i] ^ buf[pos];
        clean(b32);
    }
    // Unsafe mutable-counter API only advances whole blocks. Callers that want to
    // resume after consuming part of this block must re-run from the same counter
    // with left-padding and strip the already-consumed prefix themselves.
    return dst;
}
// AES CTR with overflowing 32 bit counter
// It's possible to do 32le significantly simpler (and probably faster) by using u32.
// But, we need both, and perf bottleneck is in ghash anyway.
// Unsafe 32-bit CTR helper: mutates `nonce` in place, expects aligned `src`/`dst`,
// and uses `isLE` to choose which 32-bit counter word is incremented.
function ctr32(xk, isLE, nonce, src, dst) {
    abytes(nonce, BLOCK_SIZE$1, 'nonce');
    abytes(src);
    dst = getOutput(src.length, dst);
    const ctr = nonce; // write new value to nonce, so it can be re-used
    const c32 = u32(ctr);
    const view = createView(ctr);
    const src32 = u32(src);
    const dst32 = u32(dst);
    // NIST SP 800-38D GCTR increments the rightmost 32 bits of J0, while
    // RFC 8452 AES-GCM-SIV increments the first 32 bits as a little-endian u32.
    const ctrPos = isLE ? 0 : 12;
    const srcLen = src.length;
    // Fill block (empty, ctr=0)
    let ctrNum = view.getUint32(ctrPos, isLE); // read current counter value
    let { s0, s1, s2, s3 } = encrypt(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3]));
    // process blocks
    for (let i = 0; i + 4 <= src32.length; i += 4) {
        dst32[i + 0] = src32[i + 0] ^ swap8IfBE(s0);
        dst32[i + 1] = src32[i + 1] ^ swap8IfBE(s1);
        dst32[i + 2] = src32[i + 2] ^ swap8IfBE(s2);
        dst32[i + 3] = src32[i + 3] ^ swap8IfBE(s3);
        ctrNum = (ctrNum + 1) >>> 0; // u32 wrap
        view.setUint32(ctrPos, ctrNum, isLE);
        ({ s0, s1, s2, s3 } = encrypt(xk, swap8IfBE(c32[0]), swap8IfBE(c32[1]), swap8IfBE(c32[2]), swap8IfBE(c32[3])));
    }
    // leftovers (less than a block)
    const start = BLOCK_SIZE$1 * Math.floor(src32.length / BLOCK_SIZE32);
    if (start < srcLen) {
        const b32 = new Uint32Array([s0, s1, s2, s3]);
        swap32IfBE(b32);
        const buf = u8(b32);
        for (let i = start, pos = 0; i < srcLen; i++, pos++)
            dst[i] = src[i] ^ buf[pos];
        clean(b32);
    }
    // Same unsafe contract as ctrCounter(): only full blocks advance the stored
    // mutable counter state; partial-block continuation is caller-managed.
    return dst;
}
function isBytes32(a) {
    // Plain `instanceof Uint32Array` is too strict for cross-realm expanded-key views.
    // This is only a best-effort unsafe-export guard, not a provenance proof for `expandKeyLE`.
    return (a instanceof Uint32Array || (ArrayBuffer.isView(a) && a.constructor.name === 'Uint32Array'));
}
// Unsafe single-block helpers: mutate `block` in place and require its 16-byte
// Uint8Array view to be 4-byte aligned because `u32(block)` reinterprets it.
function encryptBlock(xk, block) {
    abytes(block, 16, 'block');
    if (!isBytes32(xk))
        throw new Error('_encryptBlock accepts result of expandKeyLE');
    const b32 = u32(block);
    swap32IfBE(b32);
    let { s0, s1, s2, s3 } = encrypt(xk, b32[0], b32[1], b32[2], b32[3]);
    ((b32[0] = s0), (b32[1] = s1), (b32[2] = s2), (b32[3] = s3));
    swap32IfBE(b32);
    return block;
}
function decryptBlock(xk, block) {
    abytes(block, 16, 'block');
    if (!isBytes32(xk))
        throw new Error('_decryptBlock accepts result of expandKeyLE');
    const b32 = u32(block);
    swap32IfBE(b32);
    let { s0, s1, s2, s3 } = decrypt(xk, b32[0], b32[1], b32[2], b32[3]);
    ((b32[0] = s0), (b32[1] = s1), (b32[2] = s2), (b32[3] = s3));
    swap32IfBE(b32);
    return block;
}
//#region CMAC
/**
 * Left-shift by one bit and conditionally XOR with 0x87:
 * ```
 * if MSB(L) is equal to 0
 * then    K1 := L << 1;
 * else    K1 := (L << 1) XOR const_Rb;
 * ```
 *
 * Specs:
 * {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.3 | RFC 4493 Section 2.3},
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.3 | RFC 5297 Section 2.3}
 *
 * @returns modified `block` (for chaining)
 */
function dbl(block) {
    let carry = 0;
    // Left shift by 1 bit
    for (let i = BLOCK_SIZE$1 - 1; i >= 0; i--) {
        const newCarry = (block[i] & 0x80) >>> 7;
        block[i] = (block[i] << 1) | carry;
        carry = newCarry;
    }
    // XOR with 0x87 if there was a carry from the most significant bit
    if (carry) {
        // RFC 4493 §2.3 / RFC 5297 §2.1: 0x87 is const_Rb for doubling in the
        // CMAC/S2V finite field with primitive polynomial x^128 + x^7 + x^2 + x + 1.
        block[BLOCK_SIZE$1 - 1] ^= 0x87;
    }
    return block;
}
/**
 * `a XOR b`, running in-place on `a`.
 * @param a left operand and output
 * @param b right operand
 * @returns `a` (for chaining)
 */
function xorBlock(a, b) {
    if (a.length !== b.length)
        throw new Error('xorBlock: blocks must have same length');
    for (let i = 0; i < a.length; i++) {
        a[i] = a[i] ^ b[i];
    }
    return a;
}
/**
 * xorend as defined in
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.1 | RFC 5297 Section 2.1}.
 *
 * ```
 * leftmost(A, len(A)-len(B)) || (rightmost(A, len(B)) xor B)
 * ```
 *
 * Mutates `a` in place so the left prefix stays untouched and only the
 * rightmost `len(B)` bytes are xored with `b`.
 */
function xorend(a, b) {
    if (b.length > a.length) {
        throw new Error('xorend: len(B) must be less than or equal to len(A)');
    }
    // keep leftmost part of `a` unchanged
    // and xor only the rightmost part:
    const offset = a.length - b.length;
    for (let i = 0; i < b.length; i++) {
        a[offset + i] = a[offset + i] ^ b[i];
    }
    return a;
}
/**
 * Internal CMAC class.
 */
class _CMAC {
    blockLen = BLOCK_SIZE$1;
    outputLen = BLOCK_SIZE$1;
    // CMAC can only decide between `K1` and `K2` once the true final block is known,
    // so updates process older blocks eagerly but keep one pending block buffered.
    buffer;
    pos;
    finished;
    destroyed;
    k1;
    k2;
    x;
    xk;
    constructor(key) {
        abytes(key);
        validateKeyLength(key);
        this.xk = expandKeyLE(key);
        this.buffer = new Uint8Array(BLOCK_SIZE$1);
        this.pos = 0;
        this.finished = false;
        this.destroyed = false;
        this.x = new Uint8Array(BLOCK_SIZE$1);
        // L = AES_encrypt(K, const_Zero)
        const L = new Uint8Array(BLOCK_SIZE$1);
        encryptBlock(this.xk, L);
        // Generate subkeys K1 and K2 from the main key according to
        // {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.3 | RFC 4493 Section 2.3}
        // K1
        this.k1 = dbl(L);
        this.k2 = dbl(new Uint8Array(this.k1));
    }
    process(data) {
        // RFC 4493 §2.4 step 6 loop body: Y := X XOR M_i; X := AES-128(K, Y).
        xorBlock(this.x, data);
        encryptBlock(this.xk, this.x);
    }
    update(data) {
        if (this.destroyed)
            throw new Error('Hash instance has been destroyed');
        if (this.finished)
            throw new Error('Hash#digest() has already been called');
        abytes(data);
        let pos = 0;
        if (this.pos) {
            const take = Math.min(BLOCK_SIZE$1 - this.pos, data.length);
            this.buffer.set(data.subarray(0, take), this.pos);
            this.pos += take;
            pos = take;
            if (this.pos === BLOCK_SIZE$1 && pos < data.length) {
                this.process(this.buffer);
                this.pos = 0;
            }
        }
        // Keep one complete block buffered: an exact 16-byte tail may still be
        // M_n, and digestInto() must decide there whether RFC 4493 uses K1 or K2.
        while (pos + BLOCK_SIZE$1 < data.length) {
            this.process(data.subarray(pos, pos + BLOCK_SIZE$1));
            pos += BLOCK_SIZE$1;
        }
        if (pos < data.length) {
            this.buffer.set(data.subarray(pos), 0);
            this.pos = data.length - pos;
        }
        return this;
    }
    // See {@link https://www.rfc-editor.org/rfc/rfc4493.html#section-2.4 | RFC 4493 Section 2.4}.
    digestInto(out) {
        if (this.destroyed)
            throw new Error('Hash instance has been destroyed');
        if (this.finished)
            throw new Error('Hash#digest() has already been called');
        // `digestInto(out)` is the no-allocation fast path, so AES block re-use below
        // requires a 32-bit-aligned caller buffer instead of hidden temp copies.
        aoutput(out, this, true);
        this.finished = true;
        // `digestInto()` accepts out.length >= outputLen, so only the first block stores the tag.
        const view = out.subarray(0, this.outputLen);
        let last = new Uint8Array(BLOCK_SIZE$1);
        if (this.pos === BLOCK_SIZE$1) {
            // M_last := M_n XOR K1;
            last.set(this.buffer);
            xorBlock(last, this.k1);
        }
        else {
            // M_last := padding(M_n) XOR K2;
            //
            // [...] padding(x) is the concatenation of x and a single '1',
            // followed by the minimum number of '0's, so that the total length is
            // equal to 128 bits.
            last.set(this.buffer.subarray(0, this.pos));
            last[this.pos] = 0x80; // single '1' bit
            xorBlock(last, this.k2);
        }
        view.set(this.x); // X := AES_CBC(K, M_1..M_{n-1})
        xorBlock(view, last); // Y := X XOR M_last
        encryptBlock(this.xk, view); // T := AES-128(K, Y)
        clean(last);
    }
    digest() {
        const { buffer, outputLen } = this;
        this.digestInto(buffer);
        // Copy out before destroy() wipes the internal digest buffer in place.
        const res = buffer.slice(0, outputLen);
        this.destroy();
        return res;
    }
    destroy() {
        const { buffer, destroyed, x, xk, k1, k2 } = this;
        if (destroyed)
            return;
        this.destroyed = true;
        // Wipe the buffered tail, chaining value, expanded AES key, and both CMAC subkeys.
        clean(buffer, x, xk, k1, k2);
    }
}
/**
 * AES-CMAC (Cipher-based Message Authentication Code).
 * Specs: {@link https://www.rfc-editor.org/rfc/rfc4493.html | RFC 4493}.
 * @param msg - Message bytes to authenticate.
 * @param key - AES key bytes.
 * @returns 16-byte authentication tag. `cmac.create(...)` follows the same incremental MAC shape as
 * the other keyed helpers in this repo, including `blockLen`,
 * `outputLen`, `digestInto()` and `destroy()`.
 * @example
 * Authenticates a message with AES-CMAC and a fresh key.
 *
 * ```ts
 * import { cmac } from '@noble/ciphers/aes.js';
 * import { randomBytes } from '@noble/ciphers/utils.js';
 * const key = randomBytes(16);
 * cmac(new Uint8Array(), key);
 * ```
 */
// The 16-byte probe key is only used to read static metadata; runtime CMAC
// still accepts AES-128/192/256 keys.
const cmac = /* @__PURE__ */ wrapMacConstructor(16, (key) => new _CMAC(key));
/**
 * S2V (Synthetic Initialization Vector) function as described in
 * {@link https://datatracker.ietf.org/doc/html/rfc5297.html#section-2.4 | RFC 5297 Section 2.4}.
 *
 * ```
 * S2V(K, S1, ..., Sn) {
 *   if n = 0 then
 *     return V = AES-CMAC(K, <one>)
 *   fi
 *   D = AES-CMAC(K, <zero>)
 *   for i = 1 to n-1 do
 *     D = dbl(D) xor AES-CMAC(K, Si)
 *   done
 *   if len(Sn) >= 128 then
 *     T = Sn xorend D
 *   else
 *     T = dbl(D) xor pad(Sn)
 *   fi
 *   return V = AES-CMAC(K, T)
 * }
 * ```
 *
 * S2V takes a key and a vector of strings S1, S2, ..., Sn and returns a 128-bit string.
 * The S2V function is used to generate a synthetic IV for AES-SIV.
 *
 * @param key - AES key (128, 192, or 256 bits)
 * @param strings - Array of byte arrays to process
 * @returns 128-bit synthetic IV
 */
function s2v(key, strings) {
    validateKeyLength(key);
    const len = strings.length;
    if (len > 127) {
        // RFC 5297 §7 only proves S2V secure for at most 127 components; SIV
        // spends one of those on the plaintext, leaving at most 126 AAD inputs.
        throw new Error('s2v: number of input strings must be less than or equal to 127');
    }
    if (len === 0)
        return cmac(ONE_BLOCK, key);
    // D = AES-CMAC(K, <zero>)
    let d = cmac(EMPTY_BLOCK, key);
    // for i = 1 to n-1 do
    //   D = dbl(D) xor AES-CMAC(K, Si)
    for (let i = 0; i < len - 1; i++) {
        dbl(d);
        const cmacResult = cmac(strings[i], key);
        xorBlock(d, cmacResult);
        clean(cmacResult);
    }
    const s_n = strings[len - 1];
    // Earlier components are validated through cmac(...); validate the final one explicitly because
    // the Uint8Array.from()/set() paths below would otherwise coerce array-like inputs silently.
    abytes(s_n);
    let t;
    // if len(Sn) >= 128 then
    if (s_n.byteLength >= BLOCK_SIZE$1) {
        // T = Sn xorend D
        t = xorend(Uint8Array.from(s_n), d);
    }
    else {
        // pad(Sn):
        const paddedSn = new Uint8Array(BLOCK_SIZE$1);
        paddedSn.set(s_n);
        paddedSn[s_n.length] = 0x80; // padding: 0x80 followed by zeros
        // T = dbl(D) xor pad(Sn)
        t = xorBlock(dbl(d), paddedSn);
        clean(paddedSn);
    }
    // V = AES-CMAC(K, T)
    const result = cmac(t, key);
    clean(d, t);
    return result;
}
//#endregion
/**
 * Unsafe low-level internal methods. May change at any time.
 * Callers are expected to use reviewed expanded-key outputs, pass mutable and
 * aligned 16-byte blocks where required, and treat several helpers as in-place
 * mutations of their input buffers or counters.
 */
const unsafe = /* @__PURE__ */ Object.freeze({
    expandKeyLE,
    expandKeyDecLE,
    encrypt,
    decrypt,
    encryptBlock,
    decryptBlock,
    ctrCounter,
    ctr32,
    dbl,
    xorBlock,
    xorend,
    s2v,
});

const BLOCK_SIZE = 16;
const aes = {
    kind: 'cipher',
    name: 'AES',
    type: 'block',
    blockSize: BLOCK_SIZE,
    keySizes: [16, 24, 32],
    create(key) {
        return createAesCipher(key);
    },
};
const aesPreset = {
    kind: 'preset',
    name: 'aes',
    components() {
        return [aes];
    },
};
function createAesCipher(key) {
    if (key.length !== 16 && key.length !== 24 && key.length !== 32) {
        throw new Error('AES key must be 128, 192, or 256 bits.');
    }
    const encryptionKey = unsafe.expandKeyLE(key);
    const decryptionKey = unsafe.expandKeyDecLE(key);
    return {
        blockSize: BLOCK_SIZE,
        encryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock(encryptionKey, input, inputOffset, output, outputOffset, unsafe.encrypt);
        },
        decryptBlock(input, inputOffset, output, outputOffset) {
            transformBlock(decryptionKey, input, inputOffset, output, outputOffset, unsafe.decrypt);
        },
        encrypt(input, output) {
            assertBlocks(input, output);
            return transformBlocks(encryptionKey, input, output, unsafe.encrypt);
        },
        decrypt(input, output) {
            assertBlocks(input, output);
            return transformBlocks(decryptionKey, input, output, unsafe.decrypt);
        },
    };
}
function transformBlocks(expandedKey, input, output, transform) {
    for (let offset = 0; offset < input.length; offset += BLOCK_SIZE) {
        transformBlock(expandedKey, input, offset, output, offset, transform);
    }
    return output;
}
function transformBlock(expandedKey, input, inputOffset, output, outputOffset, transform) {
    const { s0, s1, s2, s3 } = transform(expandedKey, readUint32LE(input, inputOffset), readUint32LE(input, inputOffset + 4), readUint32LE(input, inputOffset + 8), readUint32LE(input, inputOffset + 12));
    writeUint32LE(output, outputOffset, s0);
    writeUint32LE(output, outputOffset + 4, s1);
    writeUint32LE(output, outputOffset + 8, s2);
    writeUint32LE(output, outputOffset + 12, s3);
}
function readUint32LE(input, offset) {
    return (input[offset]
        | (input[offset + 1] << 8)
        | (input[offset + 2] << 16)
        | (input[offset + 3] << 24)) >>> 0;
}
function writeUint32LE(output, offset, value) {
    output[offset] = value;
    output[offset + 1] = value >>> 8;
    output[offset + 2] = value >>> 16;
    output[offset + 3] = value >>> 24;
}
function assertBlocks(input, output) {
    if (input.length % BLOCK_SIZE !== 0) {
        throw new Error('AES input length must be a multiple of 128 bits.');
    }
    if (output.length !== input.length) {
        throw new Error('AES output length must equal input length.');
    }
}

export { aes, aesPreset, createAesCipher };
//# sourceMappingURL=aes.mjs.map
